import { Button, Card, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ChurnStatusChip } from '@/components/shared/ChurnStatusChip';
import { ProgramTags } from '@/components/shared/ProgramTags';
import { TestUserBadge } from '@/components/shared/TestUserBadge';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { Color } from '@/themes/MUITheme/palette';
import { ApiUser } from '@api/interfaces';
import Circle from '@mui/icons-material/Circle';
import { Stack, useTheme } from '@mui/material';
import { ChurnStatus } from '@shared/enums';
import { getAccountName, groupByDaysUntilExpiration } from "@shared/utilities";
import React from 'react';
import './styles.css';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";
import { useNavigate } from 'react-router-dom';

type CustomerCardProps = {
  customer: ApiUser;
};

const ExpiringWalletStatusMap: { [days: string]: Color } = {
  '7': 'error',
  '30': 'warning',
  '60': 'info',
  '90': 'info',
};

const CustomerCard = ({ customer }: CustomerCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accountName = getAccountName(customer);
  const theme = useTheme();
  const { isInternal, isRetailerAdmin, retailer } = useAuthentication();
  const showChurnData = isInternal || (isRetailerAdmin && retailer?.isChurnEnabled);

  const rawWalletBalances = customer.subwalletBalances
    ?.filter(balance => balance.points !== 0 && !balance.bookingId)
    .map(balance => ({
      ...balance,
      balance: balance.points,
      name: balance.subwallet.name,
    })) ?? [];

  const balancesByExpirationDays = groupByDaysUntilExpiration(rawWalletBalances);

  const ExpiringWallets = () => (
    <Stack direction="row">
      <HSpacer size="3" />
      {[7, 30, 60, 90].map((days) => (
        <Stack direction="row" key={days}>
          <HSpacer size="3" />
          <Stack maxWidth={140}>
            {!!balancesByExpirationDays[days]?.length && (
              <>
                <Stack alignItems="center" direction="row">
                  <MaterialSymbol
                    color={theme.palette[ExpiringWalletStatusMap[days]].main}
                    fill
                    icon="alarm"
                    size={16}
                  />
                  <HSpacer size="2" />
                  <Text category="label" color={ExpiringWalletStatusMap[days]} fontWeight="600">
                    {t($ => $.customers.card.daysExpiring, { count: days })}
                  </Text>
                </Stack>
                <VSpacer size="2" />
                <VSpacer size="1" />
                <Text category="body-large">
                  {t($ => $.common.units.pointsCount, {
                    count: balancesByExpirationDays[days].reduce(
                      (sum, wallet) => sum + wallet.balance, 0,
                    ),
                  })}
                </Text>
              </>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Card
      elevation={4}
      onClick={() => navigate(
        Routes.CUSTOMER_DETAILS.replace(':id', customer.id),
      )}
      sx={{ cursor: 'pointer' }}
      testID={`${customer.id}-list-item`}
    >
      <Stack alignItems="center" flexDirection="row" justifyContent="space-between">
        <Stack width={473}>
          {showChurnData && customer?.churnStatus !== ChurnStatus.NoRisk && (
            <>
              <ChurnStatusChip
                status={customer?.churnStatus as ChurnStatus}
                useCustomLabels={true}
              />
              <VSpacer size="1" />
            </>
          )}
          <ProgramTags
            isAgronomyMember={customer.isAgronomyMember}
            isEnergyMember={customer.isEnergyMember}
            testID={`customer-card-program-${customer.id}`}
          />
          <VSpacer size="2" />
          <Text category="body-xlarge">{accountName}</Text>
          <VSpacer size="1" />
          <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
            {customer.externalId ? `ERP ID: ${customer.externalId}` : `Growers ID: ${customer.id}`}
            {accountName !== customer.businessName && (
              <>
                <Circle sx={{
                  fontSize: '6px',
                  margin: '0 8px',
                  verticalAlign: 'middle',
                }} />
                {customer.businessName}
              </>
            )}
          </Text>
          {customer.isTest && (
            <>
              <VSpacer size="3" />
              <TestUserBadge />
            </>
          )}
        </Stack>
        <Stack alignItems="center" flexDirection="row">
          <ExpiringWallets />
          <HSpacer size="5" />
          <Button
            testID={`${customer.id}-navigate-button`}
            variant="outlined"
          >
            {t($ => $.customers.card.view)}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};

export default CustomerCard;
